import { useMemo, type CSSProperties } from 'react';

import { BaseSkeleton } from '../base/BaseSkeleton';
import { cn } from '../base/utils';
import { SkeletonScope, SkeletonItem, type SkeletonGroupAnimation } from '../skeleton/motion';
import { WorkbenchMasonryLayout } from './WorkbenchMasonryLayout';

export type WorkbenchCollectionLoadingVariant = 'skeleton' | 'spinner';

export interface WorkbenchCollectionSkeletonConfig {
  count?: number;
  loadMoreCount?: number;
  aspectRatios?: readonly number[];
  animated?: boolean;
  animation?: SkeletonGroupAnimation;
}

type WorkbenchCollectionSkeletonLayoutMode = 'grid' | 'masonry' | 'dense' | 'rail';

interface WorkbenchCollectionSkeletonItem {
  index: number;
  aspectRatio: number;
  key: string;
}

interface WorkbenchCollectionSkeletonProps {
  mode: WorkbenchCollectionSkeletonLayoutMode;
  phase: 'initial' | 'load-more';
  columnCount: number;
  columnWidth: number;
  cardWidth?: number | string;
  gap: number;
  rowHeight: number;
  config?: WorkbenchCollectionSkeletonConfig;
  ariaLabel: string;
  className?: string;
}

const DEFAULT_PROVISIONAL_CAPACITY = 12;
const DEFAULT_GRID_ASPECT_RATIOS = [1] as const;
const DEFAULT_RAIL_ASPECT_RATIOS = [4 / 3] as const;
const DEFAULT_MASONRY_ASPECT_RATIOS = [1, 4 / 5, 3 / 2, 3 / 4, 5 / 4] as const;
const MIN_SKELETON_ASPECT_RATIO = 0.5;
const MAX_SKELETON_ASPECT_RATIO = 2;
const SKELETON_BACKGROUND_COLOR = 'hsl(var(--monkeys-color-muted, var(--muted)))';

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function normalizeCount(value: number | undefined, fallback: number, max: number) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return fallback;
  return clamp(Math.round(value), 1, max);
}

function getDefaultAspectRatios(mode: WorkbenchCollectionSkeletonLayoutMode) {
  if (mode === 'masonry' || mode === 'dense') return DEFAULT_MASONRY_ASPECT_RATIOS;
  if (mode === 'rail') return DEFAULT_RAIL_ASPECT_RATIOS;
  return DEFAULT_GRID_ASPECT_RATIOS;
}

function resolveAspectRatios(
  mode: WorkbenchCollectionSkeletonLayoutMode,
  values: readonly number[] | undefined,
) {
  const normalized = values
    ?.filter((value) => typeof value === 'number' && Number.isFinite(value) && value > 0)
    .map((value) => clamp(value, MIN_SKELETON_ASPECT_RATIO, MAX_SKELETON_ASPECT_RATIO));
  return normalized?.length ? normalized : getDefaultAspectRatios(mode);
}

export function resolveWorkbenchCollectionSkeletonCapacity(
  config?: WorkbenchCollectionSkeletonConfig,
) {
  return normalizeCount(config?.count, DEFAULT_PROVISIONAL_CAPACITY, 48);
}

export function resolveWorkbenchCollectionLoadingVariant(
  value: unknown,
): WorkbenchCollectionLoadingVariant {
  if (value === undefined) return 'skeleton';
  if (value === 'skeleton' || value === 'spinner') return value;
  throw new TypeError('WorkbenchCollection loading.variant must be "skeleton" or "spinner".');
}

function resolveSkeletonCount({
  columnCount,
  config,
  phase,
}: Pick<WorkbenchCollectionSkeletonProps, 'columnCount' | 'config' | 'phase'>) {
  if (phase === 'initial') {
    const fallback = clamp(columnCount * 2, 6, 24);
    return normalizeCount(config?.count, fallback, 48);
  }
  const fallback = clamp(columnCount, 1, 12);
  return normalizeCount(config?.loadMoreCount, fallback, 24);
}

function getSkeletonHeight(columnWidth: number, aspectRatio: number) {
  return Math.max(1, columnWidth / aspectRatio);
}

export function WorkbenchCollectionSkeleton({
  mode,
  phase,
  columnCount,
  columnWidth,
  cardWidth,
  gap,
  rowHeight,
  config,
  ariaLabel,
  className,
}: WorkbenchCollectionSkeletonProps) {
  const count = resolveSkeletonCount({ columnCount, config, phase });
  const aspectRatios = resolveAspectRatios(mode, config?.aspectRatios);
  const animated = config?.animated ?? true;
  const items = useMemo<WorkbenchCollectionSkeletonItem[]>(
    () =>
      Array.from({ length: count }, (_, index) => ({
        aspectRatio: aspectRatios[index % aspectRatios.length],
        key: `${phase}-${index}`,
        index,
      })),
    [aspectRatios, count, phase],
  );
  const renderSkeleton = (item: WorkbenchCollectionSkeletonItem, style?: CSSProperties) => (
    <SkeletonItem index={item.index}>
      <BaseSkeleton
        variant="block"
        animated={animated}
        className="h-auto w-full rounded-lg"
        style={{
          backgroundColor: SKELETON_BACKGROUND_COLOR,
          ...(style ?? { aspectRatio: String(item.aspectRatio) }),
        }}
        data-collection-skeleton-item=""
        data-skeleton-aspect-ratio={item.aspectRatio}
      />
    </SkeletonItem>
  );

  return (
    <SkeletonScope animation={config?.animation} animated={animated}>
      <div
        role="status"
        aria-label={ariaLabel}
        aria-atomic="true"
        data-collection-loading={phase}
        data-layout-mode={mode}
        className="min-w-0"
      >
        {mode === 'masonry' || mode === 'dense' ? (
          <WorkbenchMasonryLayout
            items={items}
            columnCount={columnCount}
            columnWidth={columnWidth}
            gap={gap}
            rowHeight={rowHeight}
            placementStrategy={mode === 'dense' ? 'dense' : 'shortest'}
            className={className}
            containerProps={{ 'aria-hidden': true }}
            getItemKey={(item) => item.key}
            getItemSize={(item, layout) => ({
              height: getSkeletonHeight(layout.columnWidth, item.aspectRatio),
            })}
            renderItem={(item, layout) =>
              renderSkeleton(item, {
                height: `${getSkeletonHeight(layout.columnWidth, item.aspectRatio)}px`,
              })
            }
          />
        ) : mode === 'rail' ? (
          <div
            aria-hidden="true"
            className={cn('grid min-w-0 grid-flow-col items-start overflow-x-auto', className)}
            style={{
              gap,
              gridAutoColumns:
                typeof cardWidth === 'number' ? `${cardWidth}px` : cardWidth ?? `${columnWidth}px`,
            }}
          >
            {items.map((item) => (
              <div key={item.key}>{renderSkeleton(item)}</div>
            ))}
          </div>
        ) : (
          <div
            aria-hidden="true"
            className={cn('grid min-w-0 items-start', className)}
            style={{ gap, gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }}
          >
            {items.map((item) => (
              <div key={item.key}>{renderSkeleton(item)}</div>
            ))}
          </div>
        )}
      </div>
    </SkeletonScope>
  );
}
